'use client';

import { HttpConcern } from '@web-libs/core/http/infrastructure/client-side/use-http.concern';
import {
  AuthConcern,
  useAuthCreation,
} from '@web-libs/features/auth/main/application/use-auth.concern';
import { AuthRepository } from '@web-libs/features/auth/main/domain/auth.repository';
import { createAuthRepository } from '@web-libs/features/auth/main/api/auth.repository';
import { useEffect, useRef, useState } from 'react';
import { LoginRequestBranding } from '@web-libs/features/auth/main/domain/auth.types';
import { useTageaRouter } from '@web-libs/core/routing/application/use-tagea-router.concern';
import { createRefreshInterceptor } from '@web-libs/features/auth/main/infrastructure/token-refreshing';
import { routes } from '@web-app/[lang]/[tenant]/[institution]/routes-institution';
import { initializeAngularApp } from 'aot/angular-main';

const oidcConfigPath = process.env.NEXT_PUBLIC_OIDC_CONFIG_PATH;
const clientId = process.env.NEXT_PUBLIC_CLIENT_ID;

function addAuthInterceptor(http: HttpConcern, authConcern: AuthConcern) {
  const interceptor = createRefreshInterceptor(authConcern);
  http.addResponseInterceptor(interceptor);
}

export const useProvideAuthAngular = (
  http: HttpConcern,
  authRepository: AuthRepository,
  loginRequestBranding: LoginRequestBranding,
): [AuthConcern, boolean] => {
  const authConcern = useAuthCreation(
    authRepository,
    (token) => {
      http.setAuthToken(token);
    },
    loginRequestBranding,
    useTageaRouter(),
  );

  const interceptorAdded = useRef(false);
  const [concernReady, setConcernReady] = useState<boolean>(false);

  const router = useTageaRouter();

  const setupConcern = async () => {
    if (!http.tenantId) throw new Error('TenantId is not set in http concern');
    const logoutUrl = router.getFullLink('');
    const loginSuccessUrl = router.getFullLink(routes.dashboard);

    const angularApp = await initializeAngularApp({
      tenantId: http.tenantId,
      logoutRedirectUrl: logoutUrl,
      loginSuccessRedirectUrl: loginSuccessUrl,
      registrationUrl: router.getFullLink(routes.registration),
      passwordResetUrl: router.getFullLink(routes.resetPassword),
      logoUrl: loginRequestBranding.logoUrl,
      primaryColor: loginRequestBranding.primaryColor,
    });
    const { AuthV2Service } = await import('@tremaze/shared/core/auth-v2');
    return angularApp.injector.get(AuthV2Service);
  };

  useEffect(() => {
    setupConcern().then((service) => {
      authConcern.setAuthService(service);
      if (!interceptorAdded.current) {
        interceptorAdded.current = true;
        addAuthInterceptor(http, authConcern);
      }
      setConcernReady(true);
    });
  }, []);

  return [authConcern, concernReady];
};

export const provideAuthRepository = (http: HttpConcern): AuthRepository => {
  if (!oidcConfigPath)
    throw new Error('NEXT_PUBLIC_OIDC_CONFIG_PATH is not set');
  if (!clientId) throw new Error('NEXT_PUBLIC_CLIENT_ID is not set');
  return createAuthRepository(http, oidcConfigPath, clientId);
};
